import queryApi from "../../api/queryApi";

/**
 * Data-access layer for the backend "public query" API.
 *
 * The backend exposes exactly two query endpoints — one returns the whole
 * result set, the other returns a single page — so the functions here are
 * named after what the caller gets back (rows / a page), not after the
 * order in which they were added to the project.
 *
 * Everything that is not a query (didox and other plain REST endpoints)
 * goes through fetchResource / sendResource.
 */

/* The /api/public prefix was dropped on the backend; both spellings still
   reach the same handler and return byte-identical responses, but the
   /api/public ones answer without a token at all, so the short form is the
   one that is actually protected. */
const ROWS_URL = '/query';
const PAGE_URL = '/query/paging';

const DEFAULT_PAGE_SIZE = 50;

/* Fallback texts, kept in one place so they can be moved to i18n later.
   A message returned by the server always wins over these. */
export const errorMessages = {
    aborted: 'Запрос отменён',
    network: 'Не удалось связаться с сервером',
    server: (status) => `Ошибка сервера (${status})`,
    unknown: 'Неизвестная ошибка'
};

/* -------------------------------------------------------------- errors --- */

/**
 * Every rejection from this module is a QueryError with a readable `message`.
 * Callers can therefore write `catch (e) { setError(e.message) }` without
 * touching `e.response.data.error`, which throws a second time whenever the
 * request never reached the server (network down, cancelled, bad config).
 */
export class QueryError extends Error {
    constructor(message, {status = null, code = null, url = null, aborted = false, cause = null} = {}) {
        super(message);
        this.name = 'QueryError';
        this.status = status;
        this.code = code;
        this.url = url;
        this.aborted = aborted;
        this.cause = cause;
    }
}

/* The backend is not consistent about where it puts the text of an error. */
const readServerMessage = (data) => {
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
        for (const key of ['error', 'message', 'error_message']) {
            if (typeof data[key] === 'string' && data[key].trim()) return data[key].trim();
        }
    }
    return null;
};

const toQueryError = (error, url) => {
    if (error instanceof QueryError) return error;
    if (queryApi.isCancel(error) || error?.code === 'ERR_CANCELED') {
        return new QueryError(errorMessages.aborted, {url, code: 'ABORTED', aborted: true, cause: error});
    }
    const status = error?.response?.status ?? null;
    if (status !== null) {
        return new QueryError(readServerMessage(error.response.data) || errorMessages.server(status),
            {status, url, code: 'SERVER', cause: error});
    }
    if (error?.request) {
        return new QueryError(errorMessages.network, {url, code: 'NETWORK', cause: error});
    }
    return new QueryError(error?.message || errorMessages.unknown, {url, code: 'UNKNOWN', cause: error});
};

/* --------------------------------------------------------- query bodies --- */

const SOURCE_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
const SAFE_ARG = /^[A-Za-z0-9_.\-: ]*$/;

/**
 * Builds a `SOURCE(arg, ...)` string instead of concatenating values by hand.
 * The backend treats `source` as an SQL identifier, so an unchecked value
 * pasted in there is an injection point; arguments are validated here and
 * a bad one fails loudly on the client instead of reaching the database.
 *
 *   sourceWithArgs('GET_ACCOUNTS_BY_EMP_ID_UI_V', auth.empId)
 */
export const sourceWithArgs = (name, ...args) => {
    if (!SOURCE_NAME.test(name)) {
        throw new QueryError(`Недопустимое имя источника: ${name}`, {code: 'BAD_SOURCE'});
    }
    const rendered = args.map((arg) => {
        if (typeof arg === 'number' && Number.isFinite(arg)) return String(arg);
        if (typeof arg === 'string' && SAFE_ARG.test(arg)) return `'${arg}'`;
        throw new QueryError(`Недопустимый аргумент источника ${name}: ${arg}`, {code: 'BAD_SOURCE_ARG'});
    });
    return rendered.length ? `${name}(${rendered.join(', ')})` : name;
};

/**
 * One place that knows the request shape. Missing parts fail here with a
 * clear message instead of coming back as a 500 from the server.
 * `ID` is accepted next to `id` because the column configs use both.
 */
const buildQueryBody = ({id, ID, source, context, fields, filters = [], orderBy} = {}) => {
    if (!source) {
        throw new QueryError('В запросе не задан source', {code: 'BAD_QUERY'});
    }
    if (!Array.isArray(fields) || fields.length === 0) {
        throw new QueryError(`В запросе ${source} не заданы fields`, {code: 'BAD_QUERY'});
    }
    const query = {
        id: id || ID || source,
        source,
        fields,
        filters: Array.isArray(filters) ? filters : []
    };
    if (context) query.context = context;
    if (orderBy) query.orderBy = orderBy;
    return {query};
};

const toPositiveInt = (value, fallback) => {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : fallback;
};

/* ------------------------------------------------------- request runner --- */

/**
 * Identical requests that are still in flight share one promise.
 *
 * Two different components mounting the same tab, a StrictMode double
 * effect or a filter that settles right after mount currently produce
 * several byte-identical POSTs; sharing the promise collapses them into a
 * single network call without any change on the calling side.
 *
 * Skipped when the caller passes its own `signal`: an abort by one caller
 * must not cancel the request another caller is waiting for.
 */
const inFlight = new Map();

const runShared = (key, send) => {
    if (!key) return send();
    const pending = inFlight.get(key);
    if (pending) return pending;
    const promise = send().finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
};

const sharedKey = (url, body, params) => `${url}|${JSON.stringify(params || {})}|${JSON.stringify(body)}`;

/* --------------------------------------------------------- public API --- */

/**
 * Whole result set: POST /query
 * Always resolves to an array, so callers can .map()/.length it safely.
 */
export const selectRows = async (query, {signal, dedupe = true} = {}) => {
    const body = buildQueryBody(query);
    const key = signal || !dedupe ? null : sharedKey(ROWS_URL, body);

    return runShared(key, async () => {
        try {
            const {data} = await queryApi.post(ROWS_URL, body, {signal});
            return Array.isArray(data) ? data : [];
        } catch (error) {
            throw toQueryError(error, ROWS_URL);
        }
    });
};

/**
 * One page: POST /query/paging?linesPerPage=&pageNum=
 * Paging goes through the axios `params` option, so values are encoded
 * instead of being glued into the URL by the caller.
 * Resolves to a normalised {rows, totalCount, pageNum, linesPerPage}.
 */
export const selectPage = async (query, {pageNum = 1, linesPerPage = DEFAULT_PAGE_SIZE, signal, dedupe = true} = {}) => {
    const body = buildQueryBody(query);
    const params = {
        linesPerPage: toPositiveInt(linesPerPage, DEFAULT_PAGE_SIZE),
        pageNum: toPositiveInt(pageNum, 1)
    };
    const key = signal || !dedupe ? null : sharedKey(PAGE_URL, body, params);

    return runShared(key, async () => {
        try {
            const {data} = await queryApi.post(PAGE_URL, body, {params, signal});
            return {
                rows: Array.isArray(data?.data) ? data.data : [],
                totalCount: Number(data?.totalRowCount) || 0,
                pageNum: params.pageNum,
                linesPerPage: params.linesPerPage
            };
        } catch (error) {
            throw toQueryError(error, PAGE_URL);
        }
    });
};

/** GET for endpoints outside the query API (didox and friends). */
export const fetchResource = async (url, {params, signal, dedupe = true} = {}) => {
    const key = signal || !dedupe ? null : sharedKey(url, null, params);

    return runShared(key, async () => {
        try {
            const {data} = await queryApi.get(url, {params, signal});
            return data;
        } catch (error) {
            throw toQueryError(error, url);
        }
    });
};

/** POST for endpoints outside the query API. Never de-duplicated: a write
 *  sent twice is a different situation from a read sent twice. */
export const sendResource = async (url, body, {params, signal} = {}) => {
    try {
        const {data} = await queryApi.post(url, body, {params, signal});
        return data;
    } catch (error) {
        throw toQueryError(error, url);
    }
};
